import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../api';

const TABS = [
  { key: 'requests', label: 'Venue booking requests' },
  { key: 'today', label: 'Guest arrivals today' },
  { key: 'venues', label: 'Venue history' },
  { key: 'history', label: 'Full history' },
];

function formatDate(value) {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('en-IN', { dateStyle: 'medium' });
}

function formatDateTime(value) {
  if (!value) return '—';
  return new Date(value).toLocaleString('en-IN');
}

function statusTone(status) {
  if (status === 'approved') return { bg: 'rgba(16,185,129,0.12)', fg: '#047857' };
  if (status === 'rejected') return { bg: 'rgba(239,68,68,0.12)', fg: '#b91c1c' };
  return { bg: 'rgba(217,119,6,0.12)', fg: '#b45309' };
}

function effectiveApprovalStatus(event) {
  return event?.effectiveApprovalStatus || event?.approvalStatus || (event?.passCount > 0 ? 'approved' : 'pending');
}

function ApprovalBadge({ status }) {
  const tone = statusTone(status);
  return (
    <span className="badge" style={{ background: tone.bg, color: tone.fg }}>
      {status || 'pending'}
    </span>
  );
}

export default function AoDashboard() {
  const [data, setData] = useState({ events: [], todayVenues: [], pendingEvents: [], approvedEvents: [], rejectedEvents: [] });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('requests');

  const load = () => {
    setLoading(true);
    api
      .get('/ao/overview')
      .then((r) =>
        setData({
          events: r.data.events || [],
          todayVenues: r.data.todayVenues || [],
          pendingEvents: r.data.pendingEvents || [],
          approvedEvents: r.data.approvedEvents || [],
          rejectedEvents: r.data.rejectedEvents || [],
        }),
      )
      .catch(() => setData({ events: [], todayVenues: [], pendingEvents: [], approvedEvents: [], rejectedEvents: [] }))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const setApproval = async (id, mode) => {
    try {
      await api.post(`/ao/events/${id}/${mode}`, {});
      toast.success(mode === 'approve' ? 'Venue approved' : 'Venue rejected');
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update approval');
    }
  };

  const venueGroups = useMemo(() => {
    const map = new Map();
    [...data.events]
      .sort((a, b) => new Date(b.date) - new Date(a.date))
      .forEach((event) => {
        const venue = event.venue || 'Unknown';
        if (!map.has(venue)) map.set(venue, []);
        map.get(venue).push(event);
      });
    return Array.from(map.entries()).map(([venue, events]) => ({ venue, events }));
  }, [data.events]);

  const totalExpected = data.events.reduce((sum, event) => sum + (event.maxGuests || 0), 0);
  const totalIssued = data.events.reduce((sum, event) => sum + (event.passCount || 0), 0);
  const totalArrived = data.events.reduce((sum, event) => sum + (event.arrivedCount || 0), 0);

  if (loading) {
    return (
      <div className="page-loading">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="page-shell">
      <div className="page-header">
        <div>
          <div className="page-title">AO control room</div>
          <div className="page-subtitle">Approve venue requests, track today’s guests, and audit all venue history.</div>
        </div>
      </div>

      <div className="page-body">
        <div className="stats-grid" style={{ marginBottom: '1.25rem' }}>
          {[
            { label: 'Pending requests', value: data.pendingEvents.length },
            { label: 'Approved events', value: data.approvedEvents.length },
            { label: 'Total expected', value: totalExpected },
            { label: 'Guests arrived', value: totalArrived },
          ].map((item) => (
            <div key={item.label} className="stat-card">
              <div className="stat-value">{item.value}</div>
              <div className="stat-label">{item.label}</div>
            </div>
          ))}
        </div>

        <div className="card" style={{ marginBottom: '1.25rem' }}>
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            {TABS.map((tab) => (
              <button
                key={tab.key}
                type="button"
                className={`btn ${activeTab === tab.key ? 'btn-navy' : 'btn-ghost'} btn-sm`}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {activeTab === 'requests' && (
          <div className="card" style={{ marginBottom: '1.25rem' }}>
            <h2 className="card-heading" style={{ marginBottom: '0.75rem' }}>
              Venue booking requests
            </h2>
            {data.pendingEvents.length === 0 ? (
              <p style={{ color: 'var(--text-3)', fontSize: 'var(--text-sm)' }}>No venue requests are waiting for approval.</p>
            ) : (
              <div className="stack-gap">
                {data.pendingEvents.map((event) => (
                  <div key={event._id} className="section-card" style={{ padding: '1rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
                      <div>
                        <div style={{ fontWeight: 800, fontSize: 'var(--text-base)' }}>{event.title}</div>
                        <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-3)', marginTop: 4 }}>
                          {event.venue} · {formatDate(event.date)} · {event.faculty?.name || 'Faculty'}
                        </div>
                        <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-3)', marginTop: 4 }}>
                          Expected guests: {event.maxGuests} · Passes issued: {event.passCount || 0}
                        </div>
                      </div>
                      <ApprovalBadge status={effectiveApprovalStatus(event)} />
                    </div>
                    <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.875rem', flexWrap: 'wrap' }}>
                      <button type="button" className="btn btn-navy btn-sm" onClick={() => setApproval(event._id, 'approve')}>
                        Approve venue
                      </button>
                      <button type="button" className="btn btn-ghost btn-sm" onClick={() => setApproval(event._id, 'reject')}>
                        Reject
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'today' && (
          <div className="card" style={{ marginBottom: '1.25rem' }}>
            <h2 className="card-heading" style={{ marginBottom: '0.75rem' }}>
              Guest arrivals today
            </h2>
            {data.todayVenues.length === 0 ? (
              <p style={{ color: 'var(--text-3)', fontSize: 'var(--text-sm)' }}>No bookings listed for today.</p>
            ) : (
              <div className="stack-gap">
                {data.todayVenues.map((venue) => (
                  <div key={venue.venue} className="section-card" style={{ padding: '1rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
                      <div>
                        <div style={{ fontWeight: 800 }}>{venue.venue}</div>
                        <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-3)', marginTop: 4 }}>
                          {venue.busy ? 'Busy' : 'Open'}
                        </div>
                      </div>
                      <ApprovalBadge status={venue.busy ? 'approved' : 'pending'} />
                    </div>
                    <div className="stack-gap" style={{ marginTop: '0.75rem' }}>
                      {(venue.events || []).map((event) => (
                        <div key={event.title} style={{ padding: '0.75rem 0.875rem', borderRadius: '12px', background: 'var(--gray-50)' }}>
                          <div style={{ fontWeight: 700 }}>{event.title}</div>
                          <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-3)', marginTop: 4 }}>
                            Expected: {event.maxGuests} · Issued: {event.passCount} · Arrived: {event.arrivedCount}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'venues' && (
          <div className="card" style={{ marginBottom: '1.25rem' }}>
            <h2 className="card-heading" style={{ marginBottom: '0.75rem' }}>
              Venue history
            </h2>
            {venueGroups.length === 0 ? (
              <p style={{ color: 'var(--text-3)', fontSize: 'var(--text-sm)' }}>No venue history available yet.</p>
            ) : (
              <div className="stack-gap">
                {venueGroups.map((group) => {
                  const latest = group.events[0];
                  const totalExpectedForVenue = group.events.reduce((sum, event) => sum + (event.maxGuests || 0), 0);
                  const totalIssuedForVenue = group.events.reduce((sum, event) => sum + (event.passCount || 0), 0);
                  const totalArrivedForVenue = group.events.reduce((sum, event) => sum + (event.arrivedCount || 0), 0);
                  return (
                    <div key={group.venue} className="section-card" style={{ padding: '1rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
                        <div>
                          <div style={{ fontWeight: 800, fontSize: 'var(--text-base)' }}>{group.venue}</div>
                          <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-3)', marginTop: 4 }}>
                            {group.events.length} booking(s) · Latest on {formatDate(latest?.date)}
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                          <ApprovalBadge status={effectiveApprovalStatus(latest)} />
                        </div>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0.75rem', marginTop: '0.875rem' }}>
                        <div style={{ background: 'var(--gray-50)', borderRadius: '12px', padding: '0.75rem' }}>
                          <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-3)' }}>Expected</div>
                          <div style={{ fontWeight: 800 }}>{totalExpectedForVenue}</div>
                        </div>
                        <div style={{ background: 'var(--gray-50)', borderRadius: '12px', padding: '0.75rem' }}>
                          <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-3)' }}>Issued</div>
                          <div style={{ fontWeight: 800 }}>{totalIssuedForVenue}</div>
                        </div>
                        <div style={{ background: 'var(--gray-50)', borderRadius: '12px', padding: '0.75rem' }}>
                          <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-3)' }}>Arrived</div>
                          <div style={{ fontWeight: 800 }}>{totalArrivedForVenue}</div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {activeTab === 'history' && (
          <div className="card" style={{ marginBottom: '1.25rem' }}>
            <h2 className="card-heading" style={{ marginBottom: '0.75rem' }}>
              Full booking history
            </h2>
            {data.events.length === 0 ? (
              <p style={{ color: 'var(--text-3)', fontSize: 'var(--text-sm)' }}>No event history available yet.</p>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Event</th>
                      <th>Venue</th>
                      <th>Host</th>
                      <th>Status</th>
                      <th>Approval</th>
                      <th>Expected</th>
                      <th>Issued</th>
                      <th>Arrived</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.events.map((event) => (
                      <tr key={event._id}>
                        <td>
                          <div style={{ fontWeight: 700 }}>{formatDate(event.date)}</div>
                          <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-3)' }}>{formatDateTime(event.createdAt)}</div>
                        </td>
                        <td style={{ fontWeight: 600 }}>{event.title}</td>
                        <td>{event.venue}</td>
                        <td>
                          <div style={{ fontWeight: 600 }}>{event.faculty?.name || 'Faculty'}</div>
                          <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-3)' }}>{event.faculty?.department || ''}</div>
                        </td>
                        <td>
                          <span className={`badge badge-${event.status}`}>{event.status}</span>
                        </td>
                        <td>
                          <ApprovalBadge status={effectiveApprovalStatus(event)} />
                        </td>
                        <td>{event.maxGuests}</td>
                        <td>{event.passCount ?? 0}</td>
                        <td>{event.arrivedCount ?? 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
